import React, { useEffect, useState } from 'react';
import { filter, forEach } from 'lodash';

import { WarningBanner } from '@/components/generic/WarningBanner';
import {
  getOrderItemName,
  getOrderItemPrice,
  isRidgeCap,
} from '@/components/modals/SubmitOrder/constants';
import { useOrderContext, useOrderDispatch } from '@/components/modals/SubmitOrder/helpers';
import { OrderItemWrapper } from '@/components/modals/SubmitOrder/types';

import { OrderBlockWrapper } from '../../../../../Common/OrderBlockWrapper';
import { QuantitySelector } from '../../../QuantitySelector';
import { BlockToggle } from '../BlockToggle';

import { Props } from './types';

import styles from './RidgeCapSection.module.scss';

export const RidgeCapSection = ({ selectedRidgeCap }: Props) => {
  const state = useOrderContext();
  const dispatch = useOrderDispatch();
  const { rooferCanDecreaseQuantity, rooferCanIncreaseQuantity } = selectedRidgeCap.priceBookEntry;

  const [minQuantity, setMinQuantity] = useState(0);
  const [maxQuantity, setMaxQuantity] = useState(1000);

  useEffect(() => {
    if (!rooferCanDecreaseQuantity) {
      setMinQuantity(selectedRidgeCap.orderItem.linearFeetValue ?? 0);
    }
    if (!rooferCanIncreaseQuantity) {
      setMaxQuantity(selectedRidgeCap.orderItem.linearFeetValue ?? 1000);
    }
  }, []);

  const calculateQuantity = (linearFeet: number) => {
    const quantityRatio = selectedRidgeCap.priceBookEntry.product.linearFeetToQuantityRatio ?? 1;

    return Math.ceil(linearFeet / quantityRatio);
  };

  const calculateNumberOfBundles = (quantity: number) => {
    const bundleRatio = selectedRidgeCap.priceBookEntry.product.bundleToQuantityRatio ?? 1;

    return Math.ceil(quantity / bundleRatio);
  };

  const onLinearFeetChange = (linearFeetValue: number) => {
    const quantity = calculateQuantity(linearFeetValue);
    const numberOfBundles = calculateNumberOfBundles(quantity);
    const nonRidgeCapItems = filter(state.visibleOrderItems, oi => !isRidgeCap(oi));

    dispatch({
      type: 'UPDATE',
      payload: {
        visibleOrderItems: [
          ...nonRidgeCapItems,
          {
            orderItem: {
              ...selectedRidgeCap.orderItem,
              linearFeetValue,
              quantity,
              numberOfBundles,
            },
            priceBookEntry: selectedRidgeCap.priceBookEntry,
            proposalItem: selectedRidgeCap.proposalItem,
          },
        ],
      },
    });
  };

  const productName = getOrderItemName(selectedRidgeCap);
  const colorName = selectedRidgeCap?.priceBookEntry?.product?.asphaltShingleColor;
  const equipmentPrice = getOrderItemPrice(state, selectedRidgeCap);
  const canDecreaseQuantity = rooferCanDecreaseQuantity ?? false;
  const canIncreaseQuantity = rooferCanIncreaseQuantity ?? false;

  const onRidgeCapSectionToggle = (isEnabled: boolean) => {
    if (isEnabled) {
      const visibleItems: OrderItemWrapper[] = [...state.visibleOrderItems];
      const hiddenItems: OrderItemWrapper[] = [];

      forEach(state.hiddenOrderItems, oi => {
        const isCap = isRidgeCap(oi);

        if (isCap) {
          visibleItems.push(oi);
        } else {
          hiddenItems.push(oi);
        }
      });

      dispatch({
        type: 'UPDATE',
        payload: {
          isRidgeCapSectionEnabled: true,
          visibleOrderItems: visibleItems,
          hiddenOrderItems: hiddenItems,
        },
      });
    } else {
      const visibleItems: OrderItemWrapper[] = [];
      const hiddenItems: OrderItemWrapper[] = [];

      forEach(state.visibleOrderItems, oi => {
        const isCap = isRidgeCap(oi);

        if (isCap) {
          hiddenItems.push(oi);
        } else {
          visibleItems.push(oi);
        }
      });

      dispatch({
        type: 'UPDATE',
        payload: {
          isRidgeCapSectionEnabled: false,
          visibleOrderItems: visibleItems,
          hiddenOrderItems: hiddenItems,
        },
      });
    }
  };

  return (
    <OrderBlockWrapper color="#FAFAFA">
      <BlockToggle
        hideValue={state.isPriceHidden}
        isEnabled={state.isRidgeCapSectionEnabled}
        setIsEnabled={onRidgeCapSectionToggle}
        title={productName}
        value={equipmentPrice}
      >
        <br />
        <OrderBlockWrapper>
          <>
            <div className={styles.seal_ridge_cap_wrapper}>
              <div className={styles.seal_ridge_cap_title}>Shingle Color</div>
              <div className={styles.seal_ridge_cap_title}>Total Shipped</div>
            </div>
            <div className={styles.seal_ridge_cap_wrapper}>
              <div className={styles.seal_ridge_cap_subtitle}>{colorName}</div>
              <div className={styles.seal_ridge_cap_subtitle}>
                {selectedRidgeCap.orderItem.numberOfBundles} BD
              </div>
            </div>
          </>
        </OrderBlockWrapper>
        <br />
        <OrderBlockWrapper>
          <div className={styles.seal_ridge_cap_wrapper}>
            <div>
              <div className={styles.seal_ridge_cap_quantity_title}>Select Quantity:</div>
              <div className={styles.seal_ridge_cap_quantity_subtitle}>
                Enter the amount in LF (linear feet) and the number of bundles will be automatically
                calculated,{' '}
                <span className={styles.seal_ridge_cap_linear_feet}>
                  {selectedRidgeCap.priceBookEntry.product.linearFeetToQuantityRatio} LF per bundle.
                </span>
              </div>
            </div>
            <div className={styles.seal_ridge_cap_subtitle} style={{ width: 150 }}>
              <QuantitySelector
                canDecrease={canDecreaseQuantity}
                canIncrease={canIncreaseQuantity}
                max={maxQuantity}
                min={minQuantity}
                onChange={value => onLinearFeetChange(value)}
                value={selectedRidgeCap.orderItem.linearFeetValue ?? 0}
              />
              <span style={{ marginLeft: 15 }}>LF</span>
            </div>
          </div>
          <br />
          {!selectedRidgeCap.orderItem.linearFeetValue && (
            <WarningBanner
              subTitle={
                <span>
                  Seal-A-Ridge® Ridge Cap Shingles in Pewter Gray are{' '}
                  <span className={styles.seal_ridge_cap_only_text}>only</span> available directly
                  from GAF Energy.
                </span>
              }
              title="Please select the quantity you’ll need."
            />
          )}
        </OrderBlockWrapper>
      </BlockToggle>
    </OrderBlockWrapper>
  );
};
